import { CellValueChangedEvent, ColumnMovedEvent, ColumnResizedEvent, IRowNode } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import { useAgTable } from '../../common/datagrid/hooks/useAgTable';
import '../../common/datagrid/table-style.css';
import { PriceRow } from '../../features/price/priceTypes';
import { RootState } from '../../store';
import './pricetable.css';
import { getAgColumns, getPriceRows, getPriceTable } from './redux/priceSelectors';
import { calculateProductPrices, priceTableActions, setIsExporting } from './redux/priceSlices';
import '../../common/datagrid/table-style.css';
import './pricetable.css';

const mapStateToProps = (state: RootState) => ({
    columns: getAgColumns(state),
    priceRows: getPriceRows(state),
    priceTable: getPriceTable(state),
    isExporting: state.price.ui.isExporting
});

type Props = ReturnType<typeof mapStateToProps>;

const PriceTable = ({ columns, priceRows, priceTable, isExporting }: Props) => {
    const [rows, setRows] = useState([] as PriceRow[]);
    const [modifiedRows, setModifiedRows] = useState({} as { [index: number]: PriceRow });
    const dispatch = useDispatch();
    const gridRef = useRef<AgGridReact>(null);
    useEffect(() => {
        const mRows = {} as { [index: number]: PriceRow };
        setRows(r => {
            if (r.length === 0) {
                return priceRows;
            }

            // Extremely important to reuse old rows to prevent all
            // rows from being re-rendered every time a change occurs.
            return priceRows.map((priceRow, index) => {
                const oldRow = r.find(x => x.productId === priceRow.productId);
                if (oldRow != null && isEqual(oldRow, priceRow)) {
                    return oldRow;
                }
                mRows[index] = priceRow;
                return priceRow;
            });
        });

        setModifiedRows(mRows);
    }, [priceRows, setModifiedRows]);

    useEffect(() => {
        const rowNodes = [] as IRowNode[];

        for (const index in modifiedRows) {
            const node = gridRef?.current?.api.getDisplayedRowAtIndex(Number(index));
            if (node != null) {
                rowNodes.push(node);
            }
        }
        gridRef?.current?.api?.refreshCells({ rowNodes, force: true });
    }, [modifiedRows]);

    const onRowsChange = useCallback(
        ({ oldValue, data, rowIndex, newValue, colDef }: CellValueChangedEvent<PriceRow>) => {
            if (rowIndex != null) {
                const pricelistId = Number(colDef.field);
                const newPrice = newValue
                    ? Number((newValue.toString() as unknown as string)?.replace(',', '.'))
                    : 0;

                if (newPrice >= 0 && newValue != oldValue) {
                    dispatch(
                        calculateProductPrices({
                            currentPrice: newPrice,
                            productId: data.productId,
                            pricelistId
                        })
                    );
                }
            }
        },
        [dispatch]
    );
    const [draggedColumn, setDraggedColumn] = useState(null as { colId: string; toIndex: number } | null);

    const {
        tabbing: { tabToNextCell, handleKeyDown, navigateToNextCell },
        saveColumnWidth
    } = useAgTable<PriceRow>({
        tableSettings: priceTable,
        tableActions: priceTableActions,
        columns,
        rows,
        gridRef: gridRef
    });

    useEffect(() => {
        if (isExporting) {
            gridRef?.current?.api?.exportDataAsCsv();
            dispatch(setIsExporting(false));
        }
    }, [isExporting, dispatch]);

    return (
        <AgGridReact<PriceRow>
            className={`priceTable  ag-theme-alpine`}
            columnDefs={columns}
            rowData={rows}
            headerHeight={50}
            onFirstDataRendered={setupGrid}
            floatingFiltersHeight={30}
            onCellValueChanged={onRowsChange}
            getRowId={params => params.data.productId?.toString() ?? ''}
            onDragStopped={() => {
                if (draggedColumn != null && draggedColumn.colId != '') {
                    dispatch(
                        priceTableActions.columnsReordered({
                            sourceColumn: draggedColumn?.colId,
                            targetColumn: columns[draggedColumn?.toIndex].field ?? ''
                        })
                    );
                    setDraggedColumn(null);
                }
            }}
            onColumnEverythingChanged={setupGrid}
            onColumnResized={(event: ColumnResizedEvent<PriceRow>) => {
                if (event.finished && event.source == 'uiColumnDragged' && event.type == 'columnResized') {
                    const colState = event.columnApi.getColumnState();

                    const indexOfCol = colState.findIndex(c => c.colId == event.column?.getColId());
                    saveColumnWidth(indexOfCol, colState[indexOfCol]?.width ?? 80);
                }
            }}
            onColumnMoved={(event: ColumnMovedEvent<PriceRow>) => {
                setDraggedColumn({ colId: event.column?.getColId() ?? '', toIndex: event.toIndex ?? 0 });
            }}
            onDisplayedColumnsChanged={setupGrid}
            tabToNextCell={tabToNextCell}
            navigateToNextCell={navigateToNextCell}
            stopEditingWhenCellsLoseFocus={true}
            onCellKeyDown={handleKeyDown}
            ref={gridRef}
        />
    );
};

export default connect(mapStateToProps)(PriceTable);
